import { provideQuestionnaireGtmLabels } from "./experiments/skip-questionnaire/skip-questionnaire-labels.provider";
import {
  APP_INITIALIZER,
  ChangeDetectorRef,
  ErrorHandler,
  Injector,
  LOCALE_ID,
  NgModule,
  NgZone,
  CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";

import { RouteReuseStrategy } from "@angular/router";
import {
  DOCUMENT,
  LOCATION_INITIALIZED,
  registerLocaleData,
} from "@angular/common";
import localeEn from "@angular/common/locales/en";
import localeTh from "@angular/common/locales/th";
import localeId from "@angular/common/locales/id";
import localeEnZa from "@angular/common/locales/en-ZA";

import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { BrowserModule } from "@angular/platform-browser";

import {
  HttpClient,
  HttpClientJsonpModule,
  HttpClientModule,
  HTTP_INTERCEPTORS,
} from "@angular/common/http";

import {
  ModalController as LegacyModalController,
  PopoverController as LegacyPopoverController,
} from "@ionic/angular";
import { raf } from "@ionic/angular/common";
import {
  IonApp,
  IonContent,
  IonRefresher,
  IonRefresherContent,
  IonRouterOutlet,
  IonicRouteStrategy,
  ModalController as StandaloneModalController,
  Platform,
  PopoverController as StandalonePopoverController,
  provideIonicAngular,
} from "@ionic/angular/standalone";
import { setupConfig } from "@ionic/core";
import { defineCustomElements } from "@ionic/core/loader";
import { IonicStorageModule } from "@ionic/storage-angular";
import { Drivers } from "@ionic/storage";

import { AngularFireModule } from "@angular/fire/compat";
import { AngularFireAuthModule } from "@angular/fire/compat/auth";
import {
  AngularFireRemoteConfigModule,
  SETTINGS as REMOTE_CONFIG_SETTINGS,
} from "@angular/fire/compat/remote-config";

import {
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";

import { provideLottieOptions } from "ngx-lottie";

import { environment } from "src/environments/environment";
import { RequestInterceptorService } from "./shared/services/request-interceptor.service";
import { RetryInterceptorService } from "./shared/services/retry-interceptor.service";

import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { SHARED_MODULE_PROVIDERS } from "./shared/shared.providers";
import { ChatBubbleComponent } from "./shared/components/chat-bubble/chat-bubble.component";
import { LoaderComponent } from "./shared/components/loader/loader.component";
import { ShowOnPlatformDirective } from "./shared/directives/appShowOnPlatform.directive";
import { LearnContentModule } from "./main/pages/membership/pages/learn/core/learn-content.module";
import { take } from "rxjs";
import { StorageService } from "./shared/services/storage.service";
import { LanguageEnum } from "./shared/models/country.model";
import { LANDING_ROUTE_PATH } from "./shared/models/landing.model";
import { UserService } from "./shared/services/user.service";
import { CompositeErrorHandler } from "./composite-error-handler";
import { CleverTapStorageService } from "./shared/services/clevertap-storage.service";
import { InAppBrowser } from "@awesome-cordova-plugins/in-app-browser/ngx";
import { CleverTap } from "@awesome-cordova-plugins/clevertap/ngx";
import { PlatformService } from "./shared/services/platform.service";
import { FirebaseEventsService } from "./shared/services/firebase-events.service";
import { LoggerService } from "./shared/services/logger.service";
import { AppInitService } from "./shared/services/app-init.service";
import { MixpanelService } from "./shared/services/mixpanel.service";
import {
  initializeWithdrawalJournal,
  WithdrawalJournalInitializer,
} from "./shared/diagnostics/withdrawal-journal-initializer";
import { IntegrationTestRuntimeService } from "./testing/integration-test-runtime";
import { TraceService } from "@sentry/angular";
import { AppEntryCampaignCodeService } from "./shared/services/app-entry-campaign-code.service";
import { provideThLandingGtmLabels } from "./experiments/th-landing-variant-test/th-landing-labels.provider";

// Register supported locales
registerLocaleData(localeEn, "en");
registerLocaleData(localeTh, "th");
registerLocaleData(localeId, "id");
registerLocaleData(localeEnZa, "za-en");

const IONIC_CONFIG = {
  backButtonText: "",
  swipeBackEnabled: true,
  innerHTMLTemplatesEnabled: true,
};

export function changeDetectorRefFactory() {
  return ChangeDetectorRef;
}

/**
 * Keeps the lazy custom-element loader used by the remaining Ionic NgModules.
 *
 * Standalone Ionic wrappers register their own custom elements, whereas the
 * legacy proxies only bind Angular inputs and outputs. This bridge can be
 * removed after the remaining feature NgModules migrate to standalone Ionic.
 */
export function initializeLegacyIonicElements(
  doc: Document,
  zone: NgZone
): () => void {
  return () => {
    const win = doc.defaultView;

    if (!win || typeof window === "undefined") {
      return;
    }

    setupConfig({
      ...IONIC_CONFIG,
      _zoneGate: (handler: () => unknown) => zone.run(handler),
    });

    const useZonePatchedListener =
      "__zone_symbol__addEventListener" in doc.body;

    return defineCustomElements(win, {
      exclude: ["ion-tabs"],
      syncQueue: true,
      raf,
      jmp: (handler) => zone.runOutsideAngular(() => handler()),
      ael(element, eventName, callback, options) {
        const target = element as EventTarget & {
          __zone_symbol__addEventListener?: EventTarget["addEventListener"];
        };
        const addEventListener = useZonePatchedListener
          ? target.__zone_symbol__addEventListener
          : target.addEventListener;

        (addEventListener ?? target.addEventListener).call(
          target,
          eventName,
          callback,
          options
        );
      },
      rel(element, eventName, callback, options) {
        element.removeEventListener(eventName, callback, options);
      },
    });
  };
}

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(
    http,
    "/assets/i18n/",
    ".json?v=" + environment.translationHash
  );
}

/**
 * Checks whether the current browser URL targets a landing route
 * (e.g. `/landing` or `/landing/th`).
 *
 * Used during app bootstrap to avoid firing a competing `translate.use()`
 * call when the {@link LandingGuard} will set the correct language itself.
 *
 * @returns `true` when the pathname matches a landing route.
 */
function isLandingRoute(): boolean {
  const pathname = globalThis.location?.pathname ?? "";
  return (
    pathname === LANDING_ROUTE_PATH ||
    pathname.startsWith(`${LANDING_ROUTE_PATH}/`)
  );
}

export function ApplicationInitializerFactory(
  translate: TranslateService,
  storageService: StorageService,
  cleverTapStorageService: CleverTapStorageService,
  injector: Injector,
  integrationTestRuntime: IntegrationTestRuntimeService
) {
  return async () => {
    await injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
    const doc = injector.get(DOCUMENT);

    await storageService.init();
    await cleverTapStorageService.init();
    await integrationTestRuntime.applyStorageFixture(storageService);
    injector.get(AppInitService);
    if (environment.production) {
      injector.get(TraceService);
    }
    const user = await storageService.get("user");

    translate.addLangs(Object.values(LanguageEnum));

    const language =
      user &&
      user.language &&
      Object.values(LanguageEnum).includes(user.language)
        ? user.language
        : LanguageEnum.en;

    translate.setDefaultLang(language);
    doc.documentElement.lang = language;

    // On landing routes the LandingGuard owns translation setup.
    if (isLandingRoute()) {
      return;
    }

    try {
      translate.use(language).pipe(take(1)).subscribe();
    } catch (err) {}
  };
}

export function initializeFirebaseAnalytics(
  platformService: PlatformService,
  firebaseEventsService: FirebaseEventsService,
  loggerService: LoggerService
) {
  return async (): Promise<void> => {
    // Set Firebase Analytics enabled state based on platform
    // Only enable on Android/iOS native platforms.
    const isNativePlatform = platformService.isAndroid();

    try {
      await firebaseEventsService.setEnabled({ enabled: isNativePlatform });
    } catch (error) {
      loggerService.logError(
        `Failed to initialize Firebase Analytics ${error}`
      );
    }
  };
}

// Mixpanel initialization factory
export function initializeMixpanel(
  mixpanelService: MixpanelService
): () => Promise<void> {
  return async () => {
    await mixpanelService.init();
  };
}

export function initializeCampaignCodeEntry(
  entryService: AppEntryCampaignCodeService
): () => void {
  return () => entryService.start();
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    IonApp,
    IonContent,
    IonRefresher,
    IonRefresherContent,
    IonRouterOutlet,
    AppRoutingModule,
    TranslateModule.forRoot(),
    IonicStorageModule.forRoot({
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage],
    }),
    HttpClientModule,
    HttpClientJsonpModule,
    ChatBubbleComponent,
    LoaderComponent,
    ShowOnPlatformDirective,
    /**
     * Provides CONTENT_PROVIDER injection token at root injector.
     * LearnContentService is providedIn: 'root' but depends on CONTENT_PROVIDER token,
     * which must be provided at module level (cannot use providedIn: 'root' for tokens with factory dependencies).
     */
    LearnContentModule,
    AngularFireModule.initializeApp(environment.firebaseConfig),
    AngularFireAuthModule,
    AngularFireRemoteConfigModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
  ],
  providers: [
    provideThLandingGtmLabels(),
    provideQuestionnaireGtmLabels(),
    // Must run before provideIonicAngular so the legacy loader can add the
    // Angular zone gate to the raw Ionic configuration object.
    {
      provide: APP_INITIALIZER,
      useFactory: initializeLegacyIonicElements,
      deps: [DOCUMENT, NgZone],
      multi: true,
    },
    provideIonicAngular(IONIC_CONFIG),
    {
      provide: LegacyModalController,
      useExisting: StandaloneModalController,
    },
    {
      provide: LegacyPopoverController,
      useExisting: StandalonePopoverController,
    },
    ...SHARED_MODULE_PROVIDERS,
    ...provideLottieOptions({
      player: () => import("lottie-web/build/player/lottie_light"),
    }),
    Platform,
    {
      provide: ChangeDetectorRef,
      useFactory: changeDetectorRefFactory,
    },
    { provide: LOCALE_ID, useValue: "en" },
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      // this provider require also importing of HttpClientModule
      provide: HTTP_INTERCEPTORS,
      useClass: RequestInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptorService,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCampaignCodeEntry,
      deps: [AppEntryCampaignCodeService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: ApplicationInitializerFactory,
      deps: [
        TranslateService,
        StorageService,
        CleverTapStorageService,
        Injector,
        IntegrationTestRuntimeService,
      ],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeBetaAccessCheck,
      deps: [UserService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeWithdrawalJournal,
      deps: [WithdrawalJournalInitializer],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeMixpanel,
      deps: [MixpanelService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeFirebaseAnalytics,
      deps: [PlatformService, FirebaseEventsService, LoggerService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: CompositeErrorHandler,
    },
    {
      provide: REMOTE_CONFIG_SETTINGS,
      useFactory: () => ({
        minimumFetchIntervalMillis: environment.minimumFetchIntervalMillis,
        fetchTimeoutMillis: environment.fetchTimeoutMillis,
      }),
    },
    InAppBrowser,
    CleverTap,
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}

// Initialize function for beta access check
export function initializeBetaAccessCheck(userService: UserService) {
  return (): Promise<object> => userService.checkBetaTesterAccess();
}
